import React, { useState, useEffect } from 'react';
import { Search, Plus, MoreVertical, Edit, Trash2, Eye, EyeOff, RefreshCw, UserCheck, UserX, AlertCircle, Copy, ExternalLink, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { clientAPI } from '../../services/api';

const USER_PORTAL_URL = `${window.location.origin}/user-login`;

// Helpers for user quota color theming (avoid nested ternaries inline)
const getQuotaTone = (remaining) => {
  if (remaining <= 0) return 'red';
  if (remaining <= 3) return 'yellow';
  return 'blue';
};
const QUOTA_STYLES = {
  red:    { container: 'bg-red-500/10 border-red-500/30',    icon: 'text-red-400',    text: 'text-red-400',    bar: 'bg-red-400' },
  yellow: { container: 'bg-yellow-500/10 border-yellow-500/30', icon: 'text-yellow-400', text: 'text-yellow-400', bar: 'bg-yellow-400' },
  blue:   { container: 'bg-blue-500/10 border-blue-500/30',  icon: 'text-blue-400',   text: 'text-blue-400',   bar: 'bg-blue-400' },
};
const USER_STATUS_BADGE = {
  active:  'bg-green-400/10 text-green-400',
  pending: 'bg-yellow-400/10 text-yellow-400',
};
const getUserStatusBadge = (status) => USER_STATUS_BADGE[status] || 'bg-red-400/10 text-red-400';

const ClientUsers = () => {
  const { clientUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [userLimit, setUserLimit] = useState({ limit: 10, current: 0, remaining: 10, canCreate: true });
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    password: ''
  });

  const copyUserPortalUrl = () => {
    navigator.clipboard.writeText(USER_PORTAL_URL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    fetchUsers();
    fetchUserLimit();
  }, [clientUser]);

  const fetchUsers = async () => {
    if (!clientUser?.id) return;
    setLoading(true);
    try {
      const response = await clientAPI.getUsers(clientUser?.id);
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserLimit = async () => {
    if (!clientUser?.id) return;
    try {
      const response = await clientAPI.getUserLimit(clientUser?.id);
      setUserLimit({
        limit: response.data.user_limit,
        current: response.data.current_count,
        remaining: response.data.remaining,
        canCreate: response.data.can_create
      });
    } catch (err) {
      console.error('Error fetching user limit:', err);
    }
  };

  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!userLimit.canCreate) {
      setError(`User limit reached (${userLimit.limit}). Contact your administrator to increase your limit.`);
      return;
    }
    
    if (!formData.username || !formData.password) {
      setError('Please fill in username and password');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await clientAPI.createUser(clientUser?.id, {
        username: formData.username,
        name: formData.name || formData.username,
        password: formData.password,
        parent_id: clientUser?.id
      });
      setSuccess(`User created successfully. ${response.data.users_remaining} slots remaining.`);
      setShowModal(false);
      setFormData({ username: '', name: '', password: '' });
      fetchUsers();
      fetchUserLimit();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    
    setLoading(true);
    try {
      const updateData = {};
      if (formData.username) updateData.username = formData.username;
      if (formData.name) updateData.name = formData.name;
      if (formData.password) updateData.password = formData.password;
      
      await clientAPI.updateUser(clientUser?.id, editingUser.id, updateData);
      setSuccess('User updated successfully');
      setShowEditModal(false);
      setEditingUser(null);
      setFormData({ username: '', name: '', password: '' });
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    setLoading(true);
    try {
      await clientAPI.updateUser(clientUser?.id, userId, { status: newStatus });
      setSuccess(`User ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update user status');
    } finally {
      setLoading(false);
      setActionMenu(null);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    setLoading(true);
    try {
      await clientAPI.deleteUser(clientUser?.id, userId);
      setSuccess('User deleted successfully');
      fetchUsers();
      fetchUserLimit();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    } finally {
      setLoading(false);
      setActionMenu(null);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      name: user.name || '',
      password: ''
    });
    setShowEditModal(true);
    setActionMenu(null);
  };

  return (
    <div className="p-8" data-testid="client-users-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">User Management</h1>
          <p className="text-gray-400">Create and manage users with username and password</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={copyUserPortalUrl}
            className={`flex items-center gap-2 border px-4 py-2 rounded-lg transition-colors ${
              copied 
                ? 'border-green-500 text-green-400 bg-green-500/10' 
                : 'border-gray-700 text-gray-300 hover:bg-gray-800'
            }`}
            data-testid="copy-user-portal-url"
          >
            {copied ? (
              <>
                <Copy className="w-5 h-5" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-5 h-5" />
                Copy User Portal URL
              </>
            )}
          </button>
          <button
            onClick={() => window.open(USER_PORTAL_URL, '_blank')}
            className="flex items-center gap-2 border border-gray-700 text-gray-300 hover:bg-gray-800 px-4 py-2 rounded-lg transition-colors"
            data-testid="open-user-portal"
          >
            <ExternalLink className="w-5 h-5" />
            Open Portal
          </button>
          <button
            onClick={() => { fetchUsers(); fetchUserLimit(); }}
            disabled={loading}
            className="flex items-center gap-2 border border-gray-700 text-gray-300 hover:bg-gray-800 px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => {
              if (!userLimit.canCreate) {
                setError(`User limit reached (${userLimit.limit}). Contact your administrator.`);
                return;
              }
              setFormData({ username: '', name: '', password: '' });
              setShowModal(true);
            }}
            disabled={!userLimit.canCreate}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              userLimit.canCreate 
                ? 'bg-teal-400 hover:bg-teal-500 text-black' 
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="add-user-btn"
          >
            <Plus className="w-5 h-5" />
            Add User
          </button>
        </div>
      </div>

      {/* User Portal Info Banner */}
      <div className="mb-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-500/20 rounded-lg">
            <Users className="w-6 h-6 text-purple-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-purple-400 font-semibold mb-1">End-User Login Portal</h3>
            <p className="text-gray-400 text-sm">
              Share this URL with your users. They can login using their <span className="text-white font-medium">username</span> and <span className="text-white font-medium">password</span>.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <code className="bg-[#1a1a1f] px-3 py-1.5 rounded text-teal-400 text-sm font-mono">
                {USER_PORTAL_URL}
              </code>
              <button
                onClick={copyUserPortalUrl}
                className="p-1.5 hover:bg-gray-800 rounded transition-colors"
                title="Copy URL"
              >
                <Copy className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* User Limit Banner */}
      {(() => {
        const quotaStyle = QUOTA_STYLES[getQuotaTone(userLimit.remaining)];
        return (
      <div className={`mb-6 p-4 rounded-xl border ${quotaStyle.container}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className={`w-5 h-5 ${quotaStyle.icon}`} />
            <div>
              <p className={`font-medium ${quotaStyle.text}`}>
                User Quota: {userLimit.current} / {userLimit.limit}
              </p>
              <p className="text-gray-400 text-sm">
                {userLimit.remaining <= 0 
                  ? 'No slots remaining. Contact your administrator to increase your limit.'
                  : `${userLimit.remaining} user slot${userLimit.remaining !== 1 ? 's' : ''} remaining`
                }
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="w-32 bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${quotaStyle.bar}`}
                style={{ width: `${Math.min((userLimit.current / userLimit.limit) * 100, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>
        );
      })()}

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-400 hover:text-red-300">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400">
          {success}
        </div>
      )}

      {/* Search */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search users by username or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg pl-12 pr-4 py-2.5 text-white placeholder-gray-500 outline-none focus:border-teal-400"
            data-testid="search-users-input"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1a1a1f]">
              <tr>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">USER</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">USERNAME</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">CREATED</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">STATUS</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    {loading ? 'Loading users...' : 'No users found. Click "Add User" to create one.'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="border-t border-gray-800 hover:bg-[#1a1a1f]/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold">
                          {(user.name || user.username)?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name || user.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300 font-mono bg-[#1a1a1f] px-2 py-1 rounded">{user.username}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-300">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getUserStatusBadge(user.status)}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="relative">
                        <button
                          onClick={() => setActionMenu(actionMenu === user.id ? null : user.id)}
                          className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                          data-testid={`user-action-menu-${user.id}`}
                        >
                          <MoreVertical className="w-5 h-5 text-gray-400" />
                        </button>
                        {actionMenu === user.id && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-[#1a1a1f] border border-gray-700 rounded-lg shadow-xl z-10">
                            <button
                              onClick={() => openEditModal(user)}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-gray-300 hover:bg-gray-800 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                              Edit User
                            </button>
                            {user.status === 'active' ? (
                              <button
                                onClick={() => handleStatusChange(user.id, 'suspended')}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-yellow-400 hover:bg-gray-800 transition-colors"
                              >
                                <UserX className="w-4 h-4" />
                                Suspend User
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStatusChange(user.id, 'active')}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-green-400 hover:bg-gray-800 transition-colors"
                              >
                                <UserCheck className="w-4 h-4" />
                                Activate User
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-red-400 hover:bg-gray-800 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete User
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-2">Create New User</h3>
            <p className="text-gray-500 text-sm mb-6">
              {userLimit.remaining} of {userLimit.limit} slots available
            </p>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Username *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter username"
                  required
                  data-testid="create-user-username"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Display Name (Optional)</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter display name"
                  data-testid="create-user-name"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 pr-12 text-white outline-none focus:border-teal-400"
                    placeholder="Enter password (min 6 characters)"
                    required
                    minLength={6}
                    data-testid="create-user-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-700 text-gray-300 py-2.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black py-2.5 rounded-lg font-medium transition-colors"
                  data-testid="create-user-submit"
                >
                  {loading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-6">Edit User</h3>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter username"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Display Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter display name"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">New Password (leave blank to keep current)</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 pr-12 text-white outline-none focus:border-teal-400"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                  }}
                  className="flex-1 border border-gray-700 text-gray-300 py-2.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black py-2.5 rounded-lg font-medium transition-colors"
                >
                  {loading ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientUsers;
